import React, { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { toAbsoluteUrl } from '_metronic/helpers'
import { KTSVG } from '_metronic/helpers'
import { StepperComponent } from '_metronic/assets/ts/components'

const Home: React.FC = () => {
  const stepperRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    // Initialize stepper
    if (stepperRef.current) {
      const stepper = StepperComponent.createInsance(stepperRef.current)
      if (stepper) {
        // Handle stepper clicks
        stepper.on('kt.stepper.next', function () {
          stepper.goNext()
        })

        stepper.on('kt.stepper.previous', function () {
          stepper.goPrev()
        })
      }
    }
  }, [])

  return (
    <div className='container-xxl'>
      {/* Hero Section with Metronic Card */}
      <div className='card mb-5 mb-xl-8 bg-light'>
        <div
          className='card-body d-flex flex-column p-12'
          style={{
            backgroundImage: `url(${toAbsoluteUrl('/media/patterns/bg.png')})`,
            backgroundPosition: 'left top',
            backgroundRepeat: 'no-repeat',
            backgroundColor: 'white'
          }}
        >
          <div className='d-flex flex-center flex-column'>
            <img
              alt='Logo'
              src={toAbsoluteUrl('cover_logo.jpg')}
              className='h-200px'
            />
            <h1 className='fs-2hx text-dark fw-bolder text-center mb-7'>
              Welcome to Video Job Fairs
            </h1>
            <div className='fs-3 text-gray-800 text-center mb-13'>
              Transform Your Hiring Process with
              <span className='fw-bold text-primary ms-2'>Virtual Job Fairs</span>
            </div>
          </div>
        </div>
      </div>

      {/* Steps Section using Metronic Stepper */}
      <div className='card'>
        <div className='card-body'>
          <div className='stepper stepper-links d-flex flex-column' ref={stepperRef}>
            <h2 className='fw-bold text-dark mb-10 text-center'>
              Create Your Own Video Job Fair Event
            </h2>

            <div className='stepper-nav mb-10'>
              <div className='stepper-item'>
                <div className='stepper-wrapper'>
                  <div className='stepper-icon rounded-3'>
                    <KTSVG path='/media/icons/duotone/Communication/Group.svg' className='svg-icon svg-icon-2x' />
                  </div>
                  <div className='stepper-label'>
                    <h3 className='stepper-title'>Account Setup</h3>
                    <div className='stepper-desc'>Create employer account & company profile</div>
                  </div>
                </div>
              </div>

              <div className='stepper-item'>
                <div className='stepper-wrapper'>
                  <div className='stepper-icon'>
                    <KTSVG path='/media/icons/duotone/Code/Time-schedule.svg' className='svg-icon svg-icon-2x' />
                  </div>
                  <div className='stepper-label'>
                    <h3 className='stepper-title'>Event Registration</h3>
                    <div className='stepper-desc'>Schedule and payment</div>
                  </div>
                </div>
              </div>

              <div className='stepper-item'>
                <div className='stepper-wrapper'>
                  <div className='stepper-icon'>
                    <KTSVG path='/media/icons/duotone/Layout/Layout-4-blocks.svg' className='svg-icon svg-icon-2x' />
                  </div>
                  <div className='stepper-label'>
                    <h3 className='stepper-title'>Booth Setup</h3>
                    <div className='stepper-desc'>Create main and sub-booths</div>
                  </div>
                </div>
              </div>

              <div className='stepper-item'>
                <div className='stepper-wrapper'>
                  <div className='stepper-icon'>
                    <KTSVG path='/media/icons/duotone/Communication/Add-user.svg' className='svg-icon svg-icon-2x' />
                  </div>
                  <div className='stepper-label'>
                    <h3 className='stepper-title'>Recruiter Management</h3>
                    <div className='stepper-desc'>Add and assign recruiters</div>
                  </div>
                </div>
              </div>

              <div className='stepper-item'>
                <div className='stepper-wrapper'>
                  <div className='stepper-icon'>
                    <KTSVG path='/media/icons/duotone/\Communication/Send.svg' className='svg-icon svg-icon-2x' />
                  </div>
                  <div className='stepper-label'>
                    <h3 className='stepper-title'>Launch Event</h3>
                    <div className='stepper-desc'>Ready for job seekers</div>
                  </div>
                </div>
              </div>
            </div>

            <div className='d-flex flex-center'>
              <Link to='/auth/registration' className='btn btn-lg btn-primary fw-bold'>
                Start Creating Your Job Fair
                <KTSVG
                  path='/media/icons/duotone/Navigation/Arrow-right.svg'
                  className='svg-icon svg-icon-3 ms-2'
                />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section with Metronic Cards */}
      <div className='row g-5 g-xl-8 mt-5'>
        <h2 className='fw-bold text-dark mb-5 text-center'>Why Choose Video Job Fairs?</h2>

        <div className='col-xl-3'>
          <div className='card card-xl-stretch mb-xl-8'>
            <div className='card-body'>
              <KTSVG
                path='/media/icons/duotune/finance/fin001.svg'
                className='svg-icon svg-icon-primary svg-icon-3x mb-5'
              />
              <h3 className='text-dark fw-bold'>Cost-Effective</h3>
              <p className='text-gray-600'>Save on venue and travel expenses</p>
            </div>
          </div>
        </div>

        <div className='col-xl-3'>
          <div className='card card-xl-stretch mb-xl-8'>
            <div className='card-body'>
              <KTSVG
                path='/media/icons/duotune/maps/map001.svg'
                className='svg-icon svg-icon-primary svg-icon-3x mb-5'
              />
              <h3 className='text-dark fw-bold'>Global Reach</h3>
              <p className='text-gray-600'>Connect with candidates worldwide</p>
            </div>
          </div>
        </div>

        <div className='col-xl-3'>
          <div className='card card-xl-stretch mb-xl-8'>
            <div className='card-body'>
              <KTSVG
                path='/media/icons/duotune/general/gen015.svg'
                className='svg-icon svg-icon-primary svg-icon-3x mb-5'
              />
              <h3 className='text-dark fw-bold'>Time-Efficient</h3>
              <p className='text-gray-600'>Screen multiple candidates quickly</p>
            </div>
          </div>
        </div>

        <div className='col-xl-3'>
          <div className='card card-xl-stretch mb-xl-8'>
            <div className='card-body'>
              <KTSVG
                path='/media/icons/duotune/graphs/gra001.svg'
                className='svg-icon svg-icon-primary svg-icon-3x mb-5'
              />
              <h3 className='text-dark fw-bold'>Data Analytics</h3>
              <p className='text-gray-600'>Track engagement and results</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer with Social Links */}
      <div className='card mt-5'>
        <div className='card-body py-5'>
          <div className='d-flex flex-center gap-5'>
            <a href='#' className='btn btn-icon btn-active-light-primary w-40px h-40px'>
              <KTSVG path='/media/icons/duotune/social/soc006.svg' className='svg-icon svg-icon-1' />
            </a>
            <a href='#' className='btn btn-icon btn-active-light-primary w-40px h-40px'>
              <KTSVG path='/media/icons/duotune/social/soc001.svg' className='svg-icon svg-icon-1' />
            </a>
            <a href='#' className='btn btn-icon btn-active-light-primary w-40px h-40px'>
              <KTSVG path='/media/icons/duotune/social/soc005.svg' className='svg-icon svg-icon-1' />
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Home
